import React from 'react';
import type { PropertyLoan } from 'bundles/connect/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/pro-solid-svg-icons';
import { Col, Row } from 'react-bootstrap-5';
import { USD } from 'helpers/formatters/number_formats';
import { formatShortDate } from '../../helpers/date_helpers';
import { isEmpty } from 'lodash';

type Props = {
  loans: PropertyLoan[];
}

function formatCurrency(amount) {
  if (!amount || amount <= 0) return '--';
  return USD.format(amount, { round: true });
}

function formatPercent(rate) {
  return rate && rate > 0 ? `${rate}%` : '--';
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  return formatShortDate(dateStr);
}

function termToString(term) {
  if (!term) return '--';

  const isYears = term > 30;
  const value = isYears ? (term / 12) : term;

  const rounded = Math.round(value * 10) / 10;
  const displayValue = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);

  const label = isYears ? 'Year' : 'Month';
  const plural = parseFloat(displayValue) === 1 ? label : `${label}s`;

  return `${displayValue} ${plural}`;
}


export default function PropertyLoans({ loans }: Props) {
  if (isEmpty(loans)) return null;

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center gap-2">
        <FontAwesomeIcon className="text-secondary" icon={faCoins} />
        <h6 className="my-0 fw-bolder">Loan Information</h6>
      </div>

      <Row className="gx-0 gap-4 align-items-start">
        {loans.map((loan, index) => (
          <Col className="bg-light rounded" key={index} md xs={12} >
            <Row className="gx-0 gap-2 p-3 align-items-center border-bottom border-white">
              <Col md xs={12}>
                <strong>{formatCurrency(loan.amount)}</strong>
                <div className="text-xs text-muted">Loan Amount</div>
              </Col>
              <Col md xs={12}>
                <strong>{loan.lender || '--'}</strong>
                <div className="text-xs text-muted">Lender</div>
              </Col>
            </Row>

            <Row className="gx-0 gap-3 py-2 px-3 align-items-center small">
              <Col md xs={12}>
                <strong className="text-xs">{termToString(loan.term)}</strong>
                <div className="text-xs text-muted">Terms</div>
              </Col>
              <Col md xs={12}>
                <strong className="text-xs">{formatPercent(loan.mortgageInterestRate)}</strong>
                <div className="text-xs text-muted">Loan Interest Rate</div>
              </Col>
              <Col md xs={12}>
                <strong className="text-xs">{formatDate(loan.startDate)}</strong>
                <div className="text-xs text-muted">Loan Start Date</div>
              </Col>
              <Col md xs={12}>
                <strong className="text-xs">{formatDate(loan.endDate)}</strong>
                <div className="text-xs text-muted">Loan End Date</div>
              </Col>
            </Row>
          </Col>
        ))}
      </Row>
    </div>
  );
}
